import { useEffect, useState, type CSSProperties } from 'react';
import { BRAND, PRODUCT_NAME } from '../brand.js';
import { createTranslate, isRtl, type Lang } from '../i18n/index.js';
import { ink, theme } from '../theme.js';

/**
 * The screens a person sees when they are not inside the application: the
 * welcome, the confirmation that they have signed out, the explanation when a
 * sign-in did not complete, and the apology when the service cannot answer.
 *
 * The first three are from design/CFOAI Access.dc.html. The fourth is not: it
 * covers a state the design did not anticipate, where someone has signed in
 * perfectly well and the service behind it has nothing to say. It follows the
 * same shape, because it is the same kind of screen — an answer to something
 * that just happened, rather than an invitation.
 *
 * Nothing here reads client data, and nothing here can: the browser has no
 * session yet, and every figure in this application arrives from /api, which
 * refuses anyone who is not signed in.
 */

export type AccessVariant = 'welcome' | 'signed-out' | 'failed' | 'unavailable';

const CONTACT_EMAIL = 'services@cfoai.cpa';
const CONTACT_PHONE = '+974 4144 7210';

/** Divider dot between the footer items. */
const FOOTER_DOT = '#D4D4D8';

/** Border of the failure mark. Softer than the text red, which is too loud at 48px. */
const FAIL_MARK_BORDER = '#EBD2D1';

/** Border of the "not available" mark, matching the warning fill. */
const WAIT_MARK_BORDER = '#EADFC2';

/**
 * What each of the outcome screens says.
 *
 * Kept as data rather than nested conditionals because they differ in every
 * field, including whether they offer help at all — and because a fourth will
 * eventually be added by someone who is not us.
 */
const OUTCOMES = {
  'signed-out': {
    mark: 'good',
    title: 'You are signed out',
    body: 'Your session on this device has ended and nothing further can be reached without signing in again.',
    action: 'Sign in again',
    help: null,
  },
  failed: {
    mark: 'bad',
    title: 'Sign-in could not be completed',
    body: 'You were not signed in. This usually means the Microsoft window was closed early, or the password was entered differently than expected. Nothing has changed on your account.',
    action: 'Try again',
    help: 'If it keeps happening, write to CFO Advisory and Accountancy and we will look into it with you:',
  },
  unavailable: {
    mark: 'wait',
    title: 'Your records cannot be reached right now',
    body: 'You are signed in correctly. The part of the service that holds your records is not answering. This is not a problem with your account, and nothing you have entered has been lost.',
    action: 'Try again',
    help: 'If it is still not working in a few minutes, tell CFO Advisory and Accountancy:',
  },
} as const;

const MARK_COLOURS = {
  good: { background: theme.goodBg, border: theme.goodBorder },
  bad: { background: theme.badBg, border: FAIL_MARK_BORDER },
  wait: { background: theme.warnBg, border: WAIT_MARK_BORDER },
} as const;

const ASSURANCES: ReadonlyArray<readonly [string, string]> = [
  [
    'Arabic first',
    'Every screen, term and document is written in Arabic. English appears only where a figure or code has to stay as Xero records it.',
  ],
  [
    'Your organisation only',
    'You see the records of your own organisation. Access is granted by your accountant and can be withdrawn at any time.',
  ],
  /*
    This used to promise that nothing was filed without CFOAI checking it
    first. That stopped being true the day the draft option was removed, and
    a reassurance on a sign-in page that the product does not keep is worse
    than no reassurance at all.

    What replaced it is the thing that is actually true, and is the better
    promise anyway: what you enter is what lands, and you do not have to
    wait on us to finish your own work.
  */
  [
    'What you file is filed',
    'What you enter goes into your books as a finished document. There is no queue at our end and nothing waiting on us before you can be paid.',
  ],
];

const primaryStyle: CSSProperties = {
  marginTop: '26px',
  width: '100%',
  maxWidth: '320px',
  background: theme.nav,
  color: '#fff',
  border: 0,
  borderRadius: theme.radiusControl,
  padding: '15px 22px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  minHeight: '48px',
};

const cardStyle: CSSProperties = {
  background: theme.surface,
  borderRadius: theme.radiusCard,
  boxShadow: theme.shadowCard,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

function pillStyle(on: boolean): CSSProperties {
  return {
    background: on ? theme.nav : 'transparent',
    color: on ? '#fff' : theme.textMuted,
    border: 0,
    borderRadius: theme.radiusPill,
    padding: '7px 16px',
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
  };
}

/**
 * The logo, on the pages somebody sees before they are signed in.
 *
 * The light lockup, because these pages sit on the pale canvas rather
 * than on the navy sidebar.
 *
 * The row follows the page direction so the logo starts where the
 * reader does. The artwork itself never mirrors — `dir` turns text
 * around, not images — so there is nothing to protect it from.
 */
function Brand({ size }: { size: 'large' | 'small' }) {
  const large = size === 'large';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: large ? '26px' : '22px',
        justifyContent: 'flex-start',
      }}
    >
      <img
        src={BRAND.lockupLight}
        alt={PRODUCT_NAME}
        style={{
          display: 'block',
          height: large ? '62px' : '38px',
          width: 'auto',
          maxWidth: '100%',
        }}
      />
    </div>
  );
}

export function AccessScreen({
  variant,
  lang,
  onLang,
  onSignIn,
}: {
  variant: AccessVariant;
  lang: Lang;
  onLang: (lang: Lang) => void;
  onSignIn: () => void;
}) {
  const t = createTranslate(lang);
  const rtl = isRtl(lang);
  const welcome = variant === 'welcome';

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        minHeight: '100vh',
        background: theme.canvas,
        color: theme.text,
        fontFamily: rtl ? theme.fontArabic : theme.fontBody,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px 24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: welcome ? '620px' : '480px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '22px',
          padding: '8px 0 26px',
        }}
      >
        {welcome ? (
          <Welcome lang={lang} onLang={onLang} onSignIn={onSignIn} t={t} />
        ) : (
          <Outcome
            variant={variant}
            onAction={variant === 'unavailable' ? () => window.location.reload() : onSignIn}
            t={t}
          />
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '6px 16px',
            fontSize: '12px',
            color: theme.textMuted,
            textAlign: 'center',
          }}
        >
          <span>{t('CFO Advisory and Accountancy')}</span>
          <span style={{ color: FOOTER_DOT }}>·</span>
          <a href={`mailto:${CONTACT_EMAIL}`} dir="ltr">
            {CONTACT_EMAIL}
          </a>
          <span style={{ color: FOOTER_DOT }}>·</span>
          <span style={{ fontFamily: theme.fontMono }} dir="ltr">
            {CONTACT_PHONE}
          </span>
        </div>
      </div>
    </div>
  );
}

function Welcome({
  lang,
  onLang,
  onSignIn,
  t,
}: {
  lang: Lang;
  onLang: (lang: Lang) => void;
  onSignIn: () => void;
  t: (s: string) => string;
}) {
  const ar = lang === 'ar';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radiusPill,
            padding: '4px',
            gap: '3px',
            boxShadow: theme.shadowCard,
          }}
        >
          <button type="button" onClick={() => onLang('ar')} style={pillStyle(ar)}>
            العربية
          </button>
          <button type="button" onClick={() => onLang('en')} style={pillStyle(!ar)}>
            English
          </button>
        </div>
      </div>

      <div style={{ ...cardStyle, padding: 'clamp(26px, 5vw, 42px) clamp(20px, 5vw, 40px)' }}>
        <Brand size="large" />

        <h1
          className="pretty-wrap"
          style={{
            margin: 0,
            fontSize: 'clamp(22px, 5.4vw, 29px)',
            fontWeight: 600,
            letterSpacing: '-.02em',
            lineHeight: 1.3,
            maxWidth: '16em',
          }}
        >
          {t('Your books made easy for you')}
        </h1>

        <button type="button" onClick={onSignIn} style={primaryStyle}>
          {t('Sign in')}
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '9px',
            marginTop: '16px',
            maxWidth: '32em',
            textAlign: 'start',
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={theme.textSubtle}
            strokeWidth="2"
            strokeLinecap="round"
            style={{ flex: '0 0 auto', marginTop: '2px' }}
            aria-hidden="true"
          >
            <rect x="4" y="11" width="16" height="9" rx="2.2" />
            <path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" />
          </svg>
          <div style={{ fontSize: '12.5px', lineHeight: 1.65, color: theme.textMuted }}>
            {t(
              'You will be taken to a Microsoft sign-in page to enter your email and password. Your password is never typed into this portal. Once you are signed in, Microsoft brings you straight back here.',
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '12px',
        }}
      >
        {ASSURANCES.map(([title, body]) => (
          <div
            key={title}
            style={{
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radiusCard,
              padding: '15px 17px',
              textAlign: 'start',
            }}
          >
            <div style={{ fontSize: '12.5px', fontWeight: 600, marginBottom: '3px' }}>
              {t(title)}
            </div>
            <div style={{ fontSize: '12px', lineHeight: 1.6, color: theme.textMuted }}>
              {t(body)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Outcome({
  variant,
  onAction,
  t,
}: {
  variant: Exclude<AccessVariant, 'welcome'>;
  onAction: () => void;
  t: (s: string) => string;
}) {
  const { mark, title, body, action, help } = OUTCOMES[variant];
  const colours = MARK_COLOURS[mark];

  const markStyle: CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: theme.radiusPill,
    background: colours.background,
    border: `1px solid ${colours.border}`,
    display: 'grid',
    placeItems: 'center',
  };

  return (
    <div style={{ ...cardStyle, padding: 'clamp(24px, 5vw, 34px) clamp(20px, 5vw, 32px)' }}>
      <Brand size="small" />

      <div style={markStyle}>
        {mark === 'good' ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke={theme.goodDeep}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        ) : mark === 'wait' ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke={theme.warnDeep}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 8v4.3l2.6 1.6" />
          </svg>
        ) : (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke={theme.bad}
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 7.5v6" />
            <path d="M12 16.8v.4" />
          </svg>
        )}
      </div>

      <h1
        className="pretty-wrap"
        style={{
          margin: '16px 0 0',
          fontSize: '20px',
          fontWeight: 600,
          letterSpacing: '-.015em',
          lineHeight: 1.35,
          maxWidth: '18em',
        }}
      >
        {t(title)}
      </h1>

      <p
        className="pretty-wrap"
        style={{
          margin: '9px 0 0',
          fontSize: '14px',
          lineHeight: 1.7,
          color: theme.textMuted,
          maxWidth: '28em',
        }}
      >
        {t(body)}
      </p>

      <button type="button" onClick={onAction} style={primaryStyle}>
        {t(action)}
      </button>

      {help && (
        <div
          style={{
            marginTop: '18px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.border}`,
            width: '100%',
            maxWidth: '30em',
            fontSize: '12.5px',
            lineHeight: 1.7,
            color: theme.textMuted,
          }}
        >
          {t(help)}{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ fontWeight: 600 }} dir="ltr">
            {CONTACT_EMAIL}
          </a>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   THE OPENING SCREEN

   What used to be here was a single "…" on an empty grey page. It covered
   two quite different waits — finding out who you are, and then fetching
   everything you can reach — and said nothing about either, so the honest
   reading from the other side of it was "this has frozen".

   The wait is real and it is sometimes long. The database behind this is
   serverless and pauses after an hour idle; the first request afterwards has
   to wake it, which has been measured at fifteen seconds and is allowed to
   take up to a minute. That is not a fault to be hidden, it is a fact to be
   explained, and it is why this screen says more as it goes on rather than
   repeating itself.

   Nothing here is fetched and nothing here is client data. It renders before
   there is a session to render from.
--------------------------------------------------------------------------- */

/** When the explanation for a slow start appears. */
const EXPLAIN_AFTER_MS = 6_000;

/** When we stop explaining and suggest doing something about it. */
const SUGGEST_AFTER_MS = 25_000;

/**
 * How long this has been going on, in the only three sizes that change what
 * the screen says. A number of seconds would re-render thirty times to no
 * visible effect.
 */
function useWaitStage(): 0 | 1 | 2 {
  const [stage, setStage] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    const explain = window.setTimeout(() => setStage(1), EXPLAIN_AFTER_MS);
    const suggest = window.setTimeout(() => setStage(2), SUGGEST_AFTER_MS);
    return () => {
      window.clearTimeout(explain);
      window.clearTimeout(suggest);
    };
  }, []);

  return stage;
}

/**
 * Three dots that rise in turn.
 *
 * Marked aria-hidden and paired with a live region below, because a
 * decorative animation announced as "image" helps nobody, while "One
 * moment. We are opening your books." read once does.
 */
function WaitDots() {
  return (
    <div aria-hidden="true" style={{ display: 'flex', gap: '7px', height: '9px' }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="wait-dot"
          style={{
            width: '9px',
            height: '9px',
            borderRadius: '50%',
            background: ink,
            animationDelay: `${i * 0.18}s`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * What someone sees between pressing sign in and arriving at their books.
 *
 * Takes the language it is given and nothing else. There is no session yet,
 * so the only thing that knows which language to use is what was chosen on
 * this device last time.
 */
export function WaitingScreen({ lang }: { lang: Lang }) {
  const t = createTranslate(lang);
  const rtl = isRtl(lang);
  const stage = useWaitStage();

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        minHeight: '100vh',
        background: theme.canvas,
        color: theme.text,
        fontFamily: rtl ? theme.fontArabic : theme.fontBody,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      <div
        style={{
          ...cardStyle,
          width: '100%',
          maxWidth: '420px',
          padding: '34px 30px 30px',
          /*
            stretch, not flex-start. On a column flex container the cross axis
            IS the inline axis, so flex-start and flex-end swap sides under
            dir="rtl" — which put the logo on the left of an Arabic page, the
            far side from where the reader starts. Full-width children with
            textAlign and the rows' own justifyContent get it right in both
            directions without asking which one we are in.
          */
          alignItems: 'stretch',
          textAlign: rtl ? 'right' : 'left',
        }}
      >
        <Brand size="small" />

        {/*
          polite rather than assertive: this is reassurance, and it should
          wait its turn behind anything the reader asked for. aria-busy so a
          screen reader describes the page as still working rather than as
          finished and nearly empty.
        */}
        <div aria-live="polite" aria-busy="true" style={{ width: '100%' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('One moment')}
          </h1>
          <p
            className="pretty-wrap"
            style={{ margin: '8px 0 0', fontSize: '14px', lineHeight: 1.75, color: theme.textMuted }}
          >
            {t('We are opening your books.')}
          </p>

          {stage >= 1 && (
            <p
              className="pretty-wrap"
              style={{
                margin: '14px 0 0',
                fontSize: '13px',
                lineHeight: 1.75,
                color: theme.textSubtle,
              }}
            >
              {stage >= 2
                ? t('Still working. If this screen is still here in another minute, refresh the page.')
                : t(
                    'The first sign-in of the day takes a little longer, because your records are being woken up.',
                  )}
            </p>
          )}
        </div>

        <div style={{ marginTop: '22px' }}>
          <WaitDots />
        </div>
      </div>

      <div
        style={{
          marginTop: '20px',
          fontSize: '12px',
          color: theme.textMuted,
          textAlign: 'center',
        }}
      >
        {t('CFO Advisory and Accountancy')}
      </div>
    </div>
  );
}
